import { Box, Typography } from "@mui/material";
import { ChartData, ChartOptions } from "chart.js";
import { useFormikContext } from "formik";
import { DateTime } from "luxon";
import { ReactElement, useMemo } from "react";
import { Chart } from "react-chartjs-2";

import {
  LaneUsage,
  ProviderRateLimitKey,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import { palette } from "~frontend/theme";

import { RateLimitFormValues } from "./types";

const CATCHALL_LANE = "SPOKE2_DEFAULT";

export default function ProviderLimitSection({
  providerRateLimitKey,
  label,
  providerLimit,
  unit,
  lanes,
  usage,
}: {
  providerRateLimitKey: ProviderRateLimitKey;
  label: string;
  providerLimit: number;
  unit: "segments" | "messages";
  lanes: string[];
  usage: LaneUsage[];
}): ReactElement {
  const chartjsData = useMemo(() => {
    const timestampSet = new Set<number>([]);
    const usageByLaneAndTimestamp = new Map<string, Map<number, number>>();

    usage.forEach(u => {
      timestampSet.add(u.timestamp as number);
      if (!usageByLaneAndTimestamp.has(u.lane)) {
        usageByLaneAndTimestamp.set(u.lane, new Map());
      }
      usageByLaneAndTimestamp.get(u.lane)!.set(u.timestamp as number, u.count);
    });

    const allTimestamps = Array.from(timestampSet).sort();
    const data: ChartData<"bar" | "line"> = {
      labels: allTimestamps.map(timestamp =>
        DateTime.fromMillis(timestamp).toFormat("hh:mm a ZZZZ")
      ),
      datasets: [
        {
          label: "Limit (per minute)",
          data: allTimestamps.map(() => providerLimit * 60),
          type: "line",
        },
        ...lanes.map(lane => {
          return {
            label: lane,
            data: allTimestamps.map(
              timestamp =>
                usageByLaneAndTimestamp.get(lane)?.get(timestamp) ?? 0
            ),
            backgroundColor:
              lane === "SPOKE1_DEFAULT" ? palette.blue : palette.navy,
          };
        }),
      ],
    };

    const options: ChartOptions<"bar" | "line"> = {
      scales: {
        x: {
          stacked: true,
        },
        y: {
          stacked: true,
          min: 0,
          ticks: {
            precision: 0,
          },
        },
      },
      plugins: {
        tooltip: {
          mode: "index",
          intersect: false,
        },
        legend: {
          display: false,
        },
      },
    };

    return { data, options };
  }, [usage, lanes, providerLimit]);

  const formik = useFormikContext<RateLimitFormValues>();

  return (
    <Box sx={{ mb: 8 }}>
      <Typography variant="h2Bold">
        {label} ({providerLimit} {unit} / second)
      </Typography>
      <Chart
        type="bar"
        data={chartjsData.data}
        options={chartjsData.options}
        height="50px"
      />
      <FlexBox sx={{ mt: 2, justifyContent: "center" }}>
        {lanes.map(lane => (
          <FlexBox key={lane} sx={{ marginRight: 3, width: "200px" }}>
            <FormikTextField
              name={`${providerRateLimitKey}|${lane}`}
              label={lane}
              type="number"
              inputProps={{ min: 0, max: providerLimit }}
              fullWidth
              disabled={lane === CATCHALL_LANE}
              required
              onChange={evt => {
                // Automatically adjust the catchall lane to make sure the sum
                // of all lanes is equal to the provider limit.
                if (lane === CATCHALL_LANE) {
                  return;
                }

                formik
                  .setFieldValue(
                    `${providerRateLimitKey}|${lane}`,
                    evt.target.value
                  )
                  .catch(() => {});

                const value = parseInt(evt.target.value, 10);
                if (isNaN(value)) {
                  return;
                }

                const otherLanes = lanes.filter(
                  l => l !== CATCHALL_LANE && l !== lane
                );
                const otherLanesTotal = otherLanes.reduce(
                  (acc, l) =>
                    acc +
                    Number(formik.values[`${providerRateLimitKey}|${l}`] ?? 0),
                  0
                );

                const catchallLaneValue =
                  providerLimit - value - otherLanesTotal;

                if (catchallLaneValue >= 0) {
                  formik
                    .setFieldValue(
                      `${providerRateLimitKey}|${CATCHALL_LANE}`,
                      providerLimit - value - otherLanesTotal
                    )
                    .catch(() => {});
                }
              }}
            />
          </FlexBox>
        ))}
      </FlexBox>
    </Box>
  );
}
